/**
 * Componente para renderização de mensagens de vídeo
 * 
 * Suporta dois formatos:
 * - Vídeo normal: Formato retangular com bordas arredondadas
 * - Vídeo PTV (Push To View): Formato circular estilo video note do Telegram
 */

import { useState, useRef } from 'react';
import { Play, Pause } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getStorageUrl } from '@/lib/storage';

interface VideoMessageBubbleProps {
  /** URL do vídeo (pode ser caminho relativo do storage ou URL completa) */
  url: string;
  /** Legenda/caption do vídeo */
  caption?: string | null;
  /** Duração em segundos */
  duration?: number;
  /** Se é vídeo PTV (circular) */
  isPtv?: boolean;
  /** URL da thumbnail (opcional) */
  thumbnailUrl?: string | null;
  /** Direção da mensagem para estilização */
  direction: 'inbound' | 'outbound';
}

export function VideoMessageBubble({
  url,
  caption,
  duration,
  isPtv = false,
  thumbnailUrl,
  direction,
}: VideoMessageBubbleProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [showControls, setShowControls] = useState(!isPtv);
  const videoRef = useRef<HTMLVideoElement>(null);
  const isOutbound = direction === 'outbound';

  // Converte caminho do storage para URL completa
  const videoUrl = getStorageUrl(url) || url;
  const thumbUrl = thumbnailUrl ? (getStorageUrl(thumbnailUrl) || thumbnailUrl) : undefined;

  // Formata duração em mm:ss
  const formatDuration = (seconds?: number) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Handler para play/pause do vídeo PTV
  const handlePtvClick = () => {
    if (!videoRef.current) return;

    if (isPlaying) {
      videoRef.current.pause();
      setIsPlaying(false);
    } else {
      videoRef.current.play();
      setIsPlaying(true);
      setShowControls(false);
    }
  };

  // Handler quando vídeo termina
  const handleEnded = () => {
    setIsPlaying(false);
    setShowControls(false);
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
    }
  };

  // Renderização do vídeo PTV (circular)
  if (isPtv) {
    return (
      <div className="flex flex-col items-center gap-1">
        {/* Container circular do vídeo */}
        <div
          className={cn(
            "relative w-[200px] h-[200px] rounded-full overflow-hidden cursor-pointer",
            "border-2",
            !isOutbound && 'border-border'
          )}
          style={isOutbound ? { borderColor: 'hsl(var(--chat-border))' } : undefined}
          onClick={handlePtvClick}
        >
          {/* Vídeo */}
          <video
            ref={videoRef}
            src={videoUrl}
            poster={thumbUrl}
            className="absolute inset-0 w-full h-full object-cover"
            onEnded={handleEnded}
            playsInline
            muted={false}
          />

          {/* Overlay com botão de play (quando pausado) */}
          {!isPlaying && (
            <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
              <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center">
                <Play className="h-6 w-6 text-primary fill-primary ml-0.5" />
              </div>
            </div>
          )}

          {/* Overlay com botão de pause (quando reproduzindo e hover) */}
          {isPlaying && (
            <div 
              className="absolute inset-0 bg-black/0 hover:bg-black/30 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity"
            >
              <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center">
                <Pause className="h-6 w-6 text-primary fill-primary" />
              </div>
            </div>
          )}
        </div>

        {/* Duração do vídeo - cor fixa WhatsApp para outbound */}
        <span 
          className={cn("text-xs", !isOutbound && 'text-muted-foreground')}
          style={isOutbound ? { color: 'hsl(var(--chat-text-secondary))' } : undefined}
        >
          {formatDuration(duration)}
        </span>

        {/* Caption se houver */}
        {caption && (
          <p 
            className={cn("text-sm text-center max-w-[200px]", !isOutbound && 'text-foreground')}
            style={isOutbound ? { color: 'hsl(var(--chat-text-primary))' } : undefined}
          >
            {caption}
          </p>
        )}
      </div>
    );
  }

  // Renderização do vídeo normal (retangular) - margin negativa para bordas finas
  return (
    <div className="space-y-1 -mx-2.5 md:-mx-4">
      <video
        ref={videoRef}
        src={videoUrl}
        poster={thumbUrl}
        controls={showControls}
        className="max-w-[320px] max-h-[320px] rounded-lg object-contain w-full"
        playsInline
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={handleEnded}
      />
      {caption && (
        <p 
          className={cn("text-sm whitespace-pre-wrap break-words", !isOutbound && 'text-foreground')}
          style={isOutbound ? { color: 'hsl(var(--chat-text-primary))' } : undefined}
        >
          {caption}
        </p>
      )}
    </div>
  );
}
